import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Footer } from "../components/Footer";
import { ListHeader } from "../components/ListHeader";
import { ListItem } from "../components/ListItem";
import { MapDrawer } from "../components/MapDrawer";
import { RegionsMap } from "../components/map/RegionsMap";
import { Navbar } from "../components/Navbar";
import { SearchModal } from "../components/SearchModal";
import { useForecast } from "../lib/api";
import { useUnit } from "../lib/hooks";
import { regionSubtitle, REGIONS } from "../lib/regions";
import { useScrollToTopOnMount } from "../lib/scrollRestoration";
import { STATIC_SPOTS } from "../lib/staticSpots";
import type { Region } from "../lib/types";
import { useMeta } from "../lib/useMeta";
import styles from "./RegionsPage.module.css";

/**
 * Home page — lists Sweden's wind regions with spot counts. Clicking a
 * region with at least one spot navigates to the spots overview filtered
 * to that region. Empty regions render greyed out and aren't clickable.
 */
export function RegionsPage() {
  const [location, setLocation] = useLocation();
  useScrollToTopOnMount(location);
  useMeta({ path: "/" });
  // Kick off the forecast fetch in the background — we don't gate any
  // UI on it. By the time the user taps a region card, the data will
  // (usually) already be warm in the TanStack Query cache, so the
  // overview page renders without an extra wait. The home screen
  // itself renders entirely from STATIC_SPOTS so first paint is
  // independent of provider latency.
  const { data } = useForecast();
  const { unit, cycleUnit } = useUnit();
  const [searchOpen, setSearchOpen] = useState(false);

  const countsByRegion = useMemo(() => {
    const out: Record<Region, number> = {
      stockholm: 0,
      "east-sweden": 0,
      "south-sweden": 0,
      "west-sweden": 0,
      "central-sweden": 0,
      gotland: 0,
      oland: 0,
      "north-sweden": 0,
    };
    for (const s of STATIC_SPOTS) out[s.region]++;
    return out;
  }, []);

  // Pass enriched spots into search once the forecast lands so result
  // rows can show today's DaySummary. Before then, fall back to the
  // bundled static list — search still works, just without the
  // per-row wind summary.
  const searchSpots = data?.spots ?? STATIC_SPOTS;

  return (
    <div className={styles.layout}>
      <div className={styles.navbarSticky}>
        <div className={styles.contentColumn}>
          <Navbar
            variant="region"
            unit={unit}
            onCycleUnit={cycleUnit}
            onOpenSearch={() => setSearchOpen(true)}
          />
        </div>
      </div>
      <div className={styles.splitWrap}>
        <div className={styles.mapWrap}>
          <div className={styles.mapInner}>
            <RegionsMap
              spots={STATIC_SPOTS}
              onRegionPick={(id) => setLocation(`/region/${id}`)}
            />
          </div>
        </div>
        <MapDrawer
          className={styles.drawerSlot}
          footer={<Footer />}
        >
          <ListHeader>Regions</ListHeader>
          <div className={styles.list}>
            {REGIONS.map((region) => {
              const count = countsByRegion[region.id];
              const empty = count === 0;
              return (
                <ListItem
                  key={region.id}
                  variant="card"
                  title={region.label}
                  subtitle={regionSubtitle(region) || undefined}
                  meta={`${count} spot${count === 1 ? "" : "s"}`}
                  onClick={
                    empty ? undefined : () => setLocation(`/region/${region.id}`)
                  }
                />
              );
            })}
          </div>
        </MapDrawer>
      </div>
      <div className={styles.xlFooter}>
        <Footer />
      </div>

      <SearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        spots={searchSpots}
        unit={unit}
      />
    </div>
  );
}
